import { useLayoutEffect } from 'react';
import type { TablePaginationProps } from '@mui/material/TablePagination';
import TablePagination from '@mui/material/TablePagination';

/** Shared page sizes for admin list tables (matches API `limit` max 200). */
export const ADMIN_TABLE_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;

const defaultRowsPerPageOptions: TablePaginationProps['rowsPerPageOptions'] = [
  ...ADMIN_TABLE_ROWS_PER_PAGE_OPTIONS,
];

const paginationSx: TablePaginationProps['sx'] = (theme) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  bgcolor: theme.palette.background.paper,
  flexShrink: 0,
  '& .MuiTablePagination-toolbar': {
    flexWrap: 'nowrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: { xs: 1.5, sm: 3 },
    minHeight: 52,
    pl: { xs: 1.5, sm: 2 },
    pr: { xs: 1.5, sm: 2 },
    py: 1,
  },
  '& .MuiTablePagination-spacer': {
    flex: '1 1 auto',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    ...theme.typography.body2,
    fontWeight: 400,
    color: theme.palette.text.primary,
    margin: 0,
  },
  '& .MuiTablePagination-select': {
    ...theme.typography.body2,
    color: theme.palette.text.primary,
    borderRadius: 1,
    py: 0.25,
    pr: '24px !important',
  },
  '& .MuiTablePagination-selectIcon': {
    color: theme.palette.text.secondary,
  },
  '& .MuiTablePagination-actions': {
    marginLeft: theme.spacing(0.5),
    flexShrink: 0,
    gap: 0,
  },
  '& .MuiTablePagination-actions .MuiIconButton-root': {
    color: theme.palette.text.primary,
    padding: theme.spacing(0.75),
  },
  '& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled': {
    color: theme.palette.action.disabled,
  },
});

export type AdminTablePaginationProps = TablePaginationProps;

export const AdminTablePagination = ({
  sx,
  slotProps,
  labelRowsPerPage = 'Rows per page:',
  rowsPerPageOptions = defaultRowsPerPageOptions,
  ...rest
}: AdminTablePaginationProps) => (
  <TablePagination
    component="div"
    labelRowsPerPage={labelRowsPerPage}
    rowsPerPageOptions={rowsPerPageOptions}
    slotProps={{
      ...slotProps,
      select: {
        size: 'small',
        variant: 'standard',
        ...slotProps?.select,
      },
    }}
    sx={[paginationSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    {...rest}
  />
);

export type AdminPagedTableFooterProps = {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  /** Called when the user picks a new page size; the footer also resets the page index to 0. */
  onRowsPerPageChange: (rowsPerPage: number) => void;
} & Partial<
  Pick<
    TablePaginationProps,
    'labelDisplayedRows' | 'labelRowsPerPage' | 'rowsPerPageOptions' | 'sx'
  >
>;

export const adminPaginationLastPageIndex = (count: number, rowsPerPage: number): number => {
  if (rowsPerPage < 1) return 0;
  return Math.max(0, Math.ceil(count / rowsPerPage) - 1);
};

/**
 * If `totalCount` shrinks, pull `page` back into range (before paint).
 * Paginated `useQuery` keys should use `placeholderData: keepPreviousData` so `totalCount` does not
 * briefly become 0 while fetching the next page (which would incorrectly clamp the page back).
 */
export const useClampListPageToTotal = (
  totalCount: number,
  rowsPerPage: number,
  page: number,
  setPage: (next: number) => void,
): void => {
  useLayoutEffect(() => {
    const max = adminPaginationLastPageIndex(totalCount, rowsPerPage);
    if (page > max) {
      setPage(max);
    }
  }, [totalCount, rowsPerPage, page, setPage]);
};

export const AdminPagedTableFooter = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  labelDisplayedRows,
  labelRowsPerPage,
  rowsPerPageOptions,
  sx,
}: AdminPagedTableFooterProps) => (
  <AdminTablePagination
    count={count}
    page={page}
    rowsPerPage={rowsPerPage}
    onPageChange={(_, nextPage) => {
      onPageChange(nextPage);
    }}
    onRowsPerPageChange={(event) => {
      const next = Number.parseInt(event.target.value, 10);
      if (!Number.isFinite(next)) return;
      onRowsPerPageChange(next);
      onPageChange(0);
    }}
    labelDisplayedRows={labelDisplayedRows}
    labelRowsPerPage={labelRowsPerPage}
    rowsPerPageOptions={rowsPerPageOptions ?? defaultRowsPerPageOptions}
    sx={sx}
  />
);
